// src/pages/RegisterUser.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Button,
  Typography,
  message,
} from "antd";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { useNavigate, useLocation } from "react-router-dom";
import "./RegisterUser.css";
import SupportContacts from "./components/SupportContacts";
import {
  completePasswordAuth,
  getAuthOptions,
  getProfile,
  checkUserCallAuth,
  passwordLogin,
  registerUser as registerUserV2,
  sendCode as sendCodeV2,
  simolLogin,
  type SimolContactPayload,
  type OtpChannel,
  verifyCode as verifyCodeV2,
  verifyPasswordResetCode,
} from "./api_v2/auth";

const { Title, Paragraph } = Typography;

/* ───────── helpers ───────── */
const LS_KEY = "simolapps_state";
const REQUEST_TIMEOUT_MS = 15000;

declare global {
  interface Window {
    SimolWebApp?: {
      ready?: () => void;
      close?: () => void;
      requestContact?: () => Promise<SimolContactPayload>;
    };
  }
}

const persistUser = (user: {
  id: number;
  name?: string;
  first_name?: string;
  last_name?: string;
  phone: string;
}) => {
  const fullName =
    user.name || [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  localStorage.setItem("userId", String(user.id));
  localStorage.setItem("userName", fullName);

  let st: any = {};
  try {
    st = JSON.parse(localStorage.getItem(LS_KEY) || "{}");
  } catch {
    st = {};
  }
  st.user = { ...(st.user || {}), ...user };
  st.isRegistered = true;
  if (typeof st.hasSubscription === "undefined") st.hasSubscription = "false";
  localStorage.setItem(LS_KEY, JSON.stringify(st));
};

/* ───────── component ───────── */
const RegisterUser: React.FC = () => {
  /* шаги: 1 — телефон / код  • 2 — ФИО + пароль • 3 — пароль после OTP */
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [usePasswordLogin, setUsePasswordLogin] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [phone, setPhone] = useState("");
  const [phoneInput, setPhoneInput] = useState("+7");
  const [channel, setChannel] = useState<OtpChannel | "password">("voice");
  const [authOptionsChecked, setAuthOptionsChecked] = useState(false);
  const [authHasPassword, setAuthHasPassword] = useState(false);
  const [showOtherChannels, setShowOtherChannels] = useState(false);
  const [referral, setReferral] = useState<string | null>(null);
  const [confirmToken, setConfirmToken] = useState<string | null>(null);
  const [passwordAuthToken, setPasswordAuthToken] = useState<string | null>(null);
  const [passwordMode, setPasswordMode] = useState<"verify" | "set">("verify");
  const [userCallPending, setUserCallPending] = useState(false);
  const [userCallRequestId, setUserCallRequestId] = useState<string | null>(null);
  const [userCallNumber, setUserCallNumber] = useState<string | null>(null);
  const [simolAuthState, setSimolAuthState] = useState<
    "idle" | "loading" | "missing_bridge" | "error"
  >("idle");
  const [simolAuthMessage, setSimolAuthMessage] = useState("");
  const userCallPollRef = useRef<number | null>(null);
  const userCallTimeoutRef = useRef<number | null>(null);
  const simolStartedRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  /* три отдельные формы — чтобы не путались поля/ошибки */
  const [phoneForm] = Form.useForm();
  const [codeForm] = Form.useForm();
  const [fioForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [resetPhoneForm] = Form.useForm();
  const [resetCodeForm] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();

  /* реферал из URL / localStorage */
  useEffect(() => {
    const p = new URLSearchParams(location.search).get("ref");
    if (p) {
      localStorage.setItem("referral_code", p);
      setReferral(p);
    } else {
      setReferral(localStorage.getItem("referral_code"));
    }
  }, [location, setReferral]);

  useEffect(() => {
    return () => {
      if (userCallPollRef.current) {
        window.clearInterval(userCallPollRef.current);
      }
      if (userCallTimeoutRef.current) {
        window.clearTimeout(userCallTimeoutRef.current);
      }
    };
  }, []);

  const digitsOnly = (value: string) => value.replace(/\D/g, "");

  const extractErrorMessage = (error: any, fallback: string) => {
    try {
      const candidate =
        error?.response?.data?.message ||
        error?.message ||
        error?.toString?.();
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate;
      }
    } catch {
      /* ignore */
    }
    return fallback;
  };

  const stopUserCallPolling = () => {
    if (userCallPollRef.current) {
      window.clearInterval(userCallPollRef.current);
      userCallPollRef.current = null;
    }
    if (userCallTimeoutRef.current) {
      window.clearTimeout(userCallTimeoutRef.current);
      userCallTimeoutRef.current = null;
    }
  };

  const finalizeLogin = useCallback(async (fallbackUser?: {
    id: number;
    name?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
  }) => {
    let resolvedUser = fallbackUser || null;

    try {
      const profile = await Promise.race([
        getProfile(),
        new Promise<null>((_, reject) =>
          window.setTimeout(() => reject(new Error("profile_timeout")), REQUEST_TIMEOUT_MS)
        ),
      ]);
      if (profile?.success && profile.user) {
        resolvedUser = profile.user;
      }
    } catch {
      /* fallback to data from login response */
    }

    if (!resolvedUser?.id) {
      message.error("Не удалось загрузить профиль пользователя.");
      return;
    }

    localStorage.removeItem("referral_code");
    persistUser({
      id: resolvedUser.id,
      name: resolvedUser.name,
      first_name: resolvedUser.first_name,
      last_name: resolvedUser.last_name,
      phone: resolvedUser.phone || phone,
    });
    navigate("/subscription");
  }, [navigate, phone]);

  useEffect(() => {
    const simolSessionToken = new URLSearchParams(location.search).get("session_token");
    if (!simolSessionToken || simolStartedRef.current) {
      return;
    }

    simolStartedRef.current = true;

    const runSimolLogin = async () => {
      setSimolAuthState("loading");
      setSimolAuthMessage("Проверяем вход через Simol...");

      try {
        window.SimolWebApp?.ready?.();

        const simolReferral =
          new URLSearchParams(location.search).get("ref") ||
          localStorage.getItem("referral_code") ||
          undefined;

        const silentResult = await simolLogin({
          session_token: simolSessionToken,
          referral_code: simolReferral,
        });

        if (silentResult.success && silentResult.session_token) {
          await finalizeLogin(silentResult.user);
          return;
        }

        if (silentResult.code !== "contact_required") {
          setSimolAuthState("error");
          setSimolAuthMessage(silentResult.message || "Не удалось войти через Simol.");
          return;
        }

        if (!window.SimolWebApp || typeof window.SimolWebApp.requestContact !== "function") {
          setSimolAuthState("missing_bridge");
          setSimolAuthMessage("Откройте кабинет через кнопку Mini App внутри Simol.");
          return;
        }

        setSimolAuthMessage("Запрашиваем разрешение Simol на передачу номера...");
        const contact = await window.SimolWebApp.requestContact();
        if (!contact?.phone) {
          setSimolAuthState("error");
          setSimolAuthMessage("Simol не вернул номер телефона.");
          return;
        }

        setSimolAuthMessage("Входим в SimolApps...");
        const contactResult = await simolLogin({
          session_token: simolSessionToken,
          contact,
          referral_code: simolReferral,
        });

        if (!contactResult.success || !contactResult.session_token) {
          setSimolAuthState("error");
          setSimolAuthMessage(contactResult.message || "Не удалось войти через Simol.");
          return;
        }

        await finalizeLogin(contactResult.user ? { ...contactResult.user, phone: contactResult.user.phone || contact.phone } : undefined);
      } catch (error: any) {
        const data = error?.response?.data;
        if (data?.code !== "contact_required") {
          setSimolAuthState("error");
          setSimolAuthMessage(extractErrorMessage(error, "Ошибка подключения. Попробуйте открыть Mini App заново."));
          return;
        }

        if (!window.SimolWebApp || typeof window.SimolWebApp.requestContact !== "function") {
          setSimolAuthState("missing_bridge");
          setSimolAuthMessage("Откройте кабинет через кнопку Mini App внутри Simol.");
          return;
        }

        try {
          const simolReferral =
            new URLSearchParams(location.search).get("ref") ||
            localStorage.getItem("referral_code") ||
            undefined;
          setSimolAuthMessage("Запрашиваем разрешение Simol на передачу номера...");
          const contact = await window.SimolWebApp.requestContact();
          if (!contact?.phone) {
            setSimolAuthState("error");
            setSimolAuthMessage("Simol не вернул номер телефона.");
            return;
          }

          setSimolAuthMessage("Входим в SimolApps...");
          const contactResult = await simolLogin({
            session_token: simolSessionToken,
            contact,
            referral_code: simolReferral,
          });
          if (!contactResult.success || !contactResult.session_token) {
            setSimolAuthState("error");
            setSimolAuthMessage(contactResult.message || "Не удалось войти через Simol.");
            return;
          }
          await finalizeLogin(contactResult.user ? { ...contactResult.user, phone: contactResult.user.phone || contact.phone } : undefined);
        } catch (contactError: any) {
          setSimolAuthState("error");
          setSimolAuthMessage(extractErrorMessage(contactError, "Разрешение не получено или произошла ошибка."));
        }
      }
    };

    runSimolLogin();
  }, [location.search, finalizeLogin]);

  /* ─── API helpers ─── */
  const sendOtpCode = async (raw: string, selectedChannel: OtpChannel = "voice") => {
    const r = await sendCodeV2(raw, selectedChannel);
    if (r.success) {
      const successMsg =
        selectedChannel === "voice"
          ? "Сейчас поступит звонок. Робот продиктует голосом 4 цифры кода."
        : selectedChannel === "user_call"
          ? "Позвоните на указанный номер для подтверждения входа."
        : selectedChannel === "max"
          ? "Код отправлен в MAX (simolapps.ru)"
          : selectedChannel === "whatsapp"
            ? "Код отправлен в WhatsApp"
          : selectedChannel === "telegram"
            ? "Код отправлен в Telegram"
            : "Код отправлен";
      message.success(r.message || successMsg);
      if (selectedChannel === "voice") {
        setCodeSent(true);
        setUserCallPending(false);
        setUserCallRequestId(null);
        setUserCallNumber(null);
        stopUserCallPolling();
      } else if (selectedChannel === "user_call" && r.request_id && r.call_number) {
        setCodeSent(true);
        setUserCallPending(true);
        setUserCallRequestId(r.request_id);
        setUserCallNumber(r.call_number);
      } else {
        setCodeSent(true);
      }
    } else {
      message.error(r.message || "Ошибка отправки кода");
    }
  };

  const resendCodeByVoice = async () => {
    const targetPhone = phone || phoneInput;
    if (!targetPhone) {
      message.error("Укажите номер телефона");
      return;
    }

    setLoading(true);
    try {
      setChannel("voice");
      await sendOtpCode(targetPhone, "voice");
    } catch (error) {
      message.error(extractErrorMessage(error, "Ошибка отправки кода"));
    } finally {
      setLoading(false);
    }
  };

  const sendVerificationCode = async () => {
    const { phone: raw } = await phoneForm.validateFields();
    setPhone(raw);
    setConfirmToken(null);
    setPasswordAuthToken(null);
    setLoading(true);
    try {
      if (!authOptionsChecked) {
        const options = await getAuthOptions(raw);
        if (!options.success) {
          message.error(options.message || "Не удалось проверить способы входа");
          return;
        }

        const hasPassword = Boolean(options.has_password);
        setAuthOptionsChecked(true);
        setAuthHasPassword(hasPassword);
        setChannel(hasPassword ? "password" : "user_call");
        setShowOtherChannels(false);
        if (!hasPassword) {
          await sendOtpCode(raw, "user_call");
        }
        return;
      }

      if (channel === "password") {
        const { password } = await phoneForm.validateFields(["password"]);
        const r = await passwordLogin({ phone: raw, password });
        if (r.success || r.status === "success") {
          await finalizeLogin(r.user ? { ...r.user, phone: raw } : undefined);
        } else {
          message.error(r.message || "Неверный телефон или пароль");
        }
        return;
      }

      await sendOtpCode(raw, channel);
      return;
    } catch (error: any) {
      message.error(extractErrorMessage(error, "Сервер недоступен. Попробуйте позже."));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifiedAuthResult = useCallback(async (r: Awaited<ReturnType<typeof verifyCodeV2>>) => {
      if (r.success || r.status === "success") {
        if (r.is_new_user) {
          if (!r.confirm_token) {
            message.error("Сервер не вернул confirm_token для регистрации.");
            return;
          }
          setConfirmToken(r.confirm_token);
          setStep(2);
          setCodeSent(false);
        } else if (r.session_token) {
          await finalizeLogin(r.user ? { ...r.user, phone } : undefined);
        } else if (r.requires_password && r.auth_token) {
          setPasswordAuthToken(r.auth_token);
          setPasswordMode(r.has_password ? "verify" : "set");
          setStep(3);
          setCodeSent(false);
        } else {
          await finalizeLogin(r.user ? { ...r.user, phone } : undefined);
        }
      } else message.error(r.message || "Неверный код");
  }, [finalizeLogin, phone]);

  const verifyCode = async () => {
    const { code } = await codeForm.validateFields();
    setLoading(true);
    try {
      const r = await verifyCodeV2(phone, code);
      await handleVerifiedAuthResult(r);
    } catch (error: any) {
      message.error(extractErrorMessage(error, "Сервер недоступен. Попробуйте позже."));
    } finally {
      setLoading(false);
    }
  };

  const pollUserCallAuth = useCallback((requestId: string, targetPhone: string) => {
    stopUserCallPolling();

    userCallPollRef.current = window.setInterval(async () => {
      try {
        const status = await checkUserCallAuth({ phone: targetPhone, request_id: requestId });
        if (!status.success || status.status === "pending") {
          return;
        }

        stopUserCallPolling();
        setUserCallPending(false);

        if (status.status === "approved") {
          message.success(status.message || "Звонок подтверждён");
          if (resetMode && status.auth_token) {
            setResetToken(status.auth_token);
            setResetCodeSent(false);
            return;
          }
          await handleVerifiedAuthResult(status);
          return;
        }

        if (status.status === "expired") {
          message.warning(status.message || "Время ожидания звонка истекло.");
          return;
        }

        message.error(status.message || "Не удалось подтвердить звонок.");
      } catch {
        // keep polling until timeout/status
      }
    }, 5000);

    userCallTimeoutRef.current = window.setTimeout(() => {
      stopUserCallPolling();
      setUserCallPending(false);
      message.warning("Время ожидания звонка истекло. Можно запросить код заново.");
    }, 5 * 60 * 1000 + 2000);
  }, [handleVerifiedAuthResult, resetMode]);

  useEffect(() => {
    if (userCallPending && userCallRequestId && phone) {
      pollUserCallAuth(userCallRequestId, phone);
    }
  }, [userCallPending, userCallRequestId, phone, pollUserCallAuth]);

  const startUserCallAuth = async () => {
    const targetPhone = phone || phoneInput;
    if (!targetPhone) {
      message.error("Укажите номер телефона");
      return;
    }

    setLoading(true);
    try {
      setChannel("user_call");
      await sendOtpCode(targetPhone, "user_call");
    } catch (error) {
      message.error(extractErrorMessage(error, "Ошибка запуска подтверждения звонком"));
    } finally {
      setLoading(false);
    }
  };

  const completeRegistration = async () => {
    const { first_name, last_name, middle_name, password } = await fioForm.validateFields();
    if (!confirmToken) {
      message.error("Сначала подтвердите номер телефона.");
      setStep(1);
      setCodeSent(false);
      return;
    }
    setLoading(true);
    try {
      const r = await registerUserV2({
        confirm_token: confirmToken,
        first_name,
        last_name,
        middle_name,
        password,
        referral_code: referral || undefined,
      });
      if (r.success || r.status === "success") {
        await finalizeLogin(
          r.user
            ? {
                ...r.user,
                first_name,
                last_name,
                phone,
              }
            : undefined
        );
      } else message.error(r.message || "Ошибка регистрации");
    } catch (error: any) {
      message.error(extractErrorMessage(error, "Сервер недоступен. Попробуйте позже."));
    } finally {
      setLoading(false);
    }
  };

  const completeOtpPasswordAuth = async () => {
    const { password } = await passwordForm.validateFields();
    if (!passwordAuthToken) {
      message.error("Сначала подтвердите номер телефона.");
      setStep(1);
      setCodeSent(false);
      return;
    }

    setLoading(true);
    try {
      const r = await completePasswordAuth({
        auth_token: passwordAuthToken,
        password,
      });
      if (r.success || r.status === "success") {
        await finalizeLogin(r.user ? { ...r.user, phone } : undefined);
      } else {
        message.error(r.message || "Неверный пароль");
      }
    } catch (error: any) {
      message.error(extractErrorMessage(error, "Сервер недоступен. Попробуйте позже."));
    } finally {
      setLoading(false);
    }
  };

  const startPasswordReset = async () => {
    const { phone: raw, channel: resetChannel } = await resetPhoneForm.validateFields();
    setPasswordLoading(true);
    try {
      const selectedChannel: OtpChannel = resetChannel || "user_call";
      const r = await sendCodeV2(raw, selectedChannel, "reset");
      if (r.success) {
        setPhone(raw);
        setResetCodeSent(true);
        if (selectedChannel === "user_call" && r.request_id && r.call_number) {
          setUserCallPending(true);
          setUserCallRequestId(r.request_id);
          setUserCallNumber(r.call_number);
        }
        message.success(r.message || (selectedChannel === "user_call" ? "Позвоните на указанный номер" : "Код отправлен"));
      } else {
        message.error(r.message || "Не удалось отправить код");
      }
    } catch (error: any) {
      message.error(extractErrorMessage(error, "Сервер недоступен. Попробуйте позже."));
    } finally {
      setPasswordLoading(false);
    }
  };

  const verifyPasswordReset = async () => {
    const { code } = await resetCodeForm.validateFields();
    setPasswordLoading(true);
    try {
      const r = await verifyPasswordResetCode({ phone, code });
      if (r.success && r.auth_token) {
        setResetToken(r.auth_token);
        message.success(r.message || "Телефон подтверждён");
      } else {
        message.error(r.message || "Неверный код");
      }
    } catch (error: any) {
      message.error(extractErrorMessage(error, "Неверный код"));
    } finally {
      setPasswordLoading(false);
    }
  };

  const saveResetPassword = async () => {
    const { password } = await resetPasswordForm.validateFields();
    if (!resetToken) {
      message.error("Сначала подтвердите код.");
      return;
    }

    setPasswordLoading(true);
    try {
      const r = await completePasswordAuth({
        auth_token: resetToken,
        password,
      });
      if (r.success || r.status === "success") {
        await finalizeLogin(r.user ? { ...r.user, phone } : undefined);
      } else {
        message.error(r.message || "Не удалось сохранить пароль");
      }
    } catch (error: any) {
      message.error(extractErrorMessage(error, "Сервер недоступен. Попробуйте позже."));
    } finally {
      setPasswordLoading(false);
    }
  };

  /* ─── JSX ─── */
  if (simolAuthState !== "idle") {
    return (
      <div className="register-user-container">
        <Title level={2}>SimolVPN</Title>
        <Paragraph type={simolAuthState === "error" || simolAuthState === "missing_bridge" ? "danger" : "secondary"}>
          {simolAuthMessage || "Подключаем Mini App..."}
        </Paragraph>
        {(simolAuthState === "error" || simolAuthState === "missing_bridge") && (
          <Button type="primary" onClick={() => window.location.reload()} block>
            Повторить
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="register-user-container">
      <Title level={2}>
        {resetMode
          ? "Восстановление пароля"
          : authOptionsChecked && authHasPassword && showOtherChannels
            ? "Другой способ"
            : authOptionsChecked && !authHasPassword
              ? "Подтвердите номер"
              : "Вход или регистрация"}
      </Title>

      {!resetMode && (
        <Paragraph type="secondary" style={{ marginBottom: 20, fontSize: 16 }}>
          {authOptionsChecked
            ? authHasPassword
              ? showOtherChannels
                ? "Подтвердите номер бесплатным звонком."
                : "Введите пароль от вашего профиля."
              : "Позвоните с указанного номера — вход подтвердится автоматически."
            : "Введите номер телефона."}
        </Paragraph>
      )}

      {step === 1 && !codeSent && usePasswordLogin && resetMode && !resetCodeSent && !resetToken && (
        <Form
          key="reset-phone"
          form={resetPhoneForm}
          layout="vertical"
          initialValues={{ phone: phoneInput, channel: "user_call" }}
          onFinish={startPasswordReset}
        >
          <Form.Item
            label="Номер телефона"
            name="phone"
            rules={[
              { required: true, message: "Введите номер" },
              {
                validator: (_, v) => {
                  const digits = digitsOnly(v || "");
                  if (digits.length < 8 || digits.length > 15) {
                    return Promise.reject(new Error("Некорректный номер"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <PhoneInput
              defaultCountry="ru"
              value={phoneInput}
              onChange={(value) => {
                setPhoneInput(value);
                resetPhoneForm.setFieldsValue({ phone: value });
              }}
            />
          </Form.Item>

          <Form.Item name="channel" hidden>
            <Input />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={passwordLoading} block>
              Позвонить для восстановления
            </Button>
            <Button
              type="link"
              className="auth-secondary-link"
              onClick={() => {
                resetPhoneForm.setFieldValue("channel", "voice");
                resetPhoneForm.submit();
              }}
              disabled={passwordLoading}
              block
            >
              Получить звонок с кодом
            </Button>
          </Form.Item>
        </Form>
      )}

      {step === 1 && !codeSent && usePasswordLogin && resetMode && resetCodeSent && !resetToken && (
        userCallPending && userCallNumber ? (
          <div className="user-call-box">
            <div className="user-call-title">Позвоните для подтверждения</div>
            <div className="user-call-text">С восстанавливаемого номера позвоните на:</div>
            <a className="user-call-number" href={`tel:+${userCallNumber}`}>
              +{userCallNumber}
            </a>
            <div className="user-call-note">После звонка откроется форма нового пароля.</div>
          </div>
        ) : (
        <Form
          key="reset-code"
          form={resetCodeForm}
          layout="vertical"
          onFinish={verifyPasswordReset}
        >
          <Form.Item
            label="Код подтверждения"
            name="code"
            rules={[
              { required: true, message: "Введите код" },
              { pattern: /^\d{4}$/, message: "Код должен состоять из 4 цифр" },
            ]}
          >
            <Input maxLength={4} inputMode="numeric" placeholder="4 цифры" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={passwordLoading} block>
              Подтвердить код
            </Button>
          </Form.Item>
        </Form>
        )
      )}

      {step === 1 && !codeSent && usePasswordLogin && resetMode && resetToken && (
        <Form
          key="reset-password"
          form={resetPasswordForm}
          layout="vertical"
          onFinish={saveResetPassword}
        >
          <Form.Item
            label="Новый пароль"
            name="password"
            rules={[
              { required: true, message: "Введите новый пароль" },
              { min: 8, message: "Минимум 8 символов" },
            ]}
            hasFeedback
          >
            <Input.Password autoComplete="new-password" placeholder="Минимум 8 символов" />
          </Form.Item>

          <Form.Item
            label="Повторите пароль"
            name="password_confirm"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Повторите пароль" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Пароли не совпадают"));
                },
              }),
            ]}
            hasFeedback
          >
            <Input.Password autoComplete="new-password" placeholder="Повторите пароль" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={passwordLoading} block>
              Сохранить новый пароль
            </Button>
          </Form.Item>
        </Form>
      )}

      {/* ───── Шаг 1. Телефон ───── */}
      {step === 1 && !codeSent && !usePasswordLogin && !resetMode && (
        <Form
          key="phone"
          form={phoneForm}
          layout="vertical"
          initialValues={{ phone: phoneInput }}
          onFinish={sendVerificationCode}
        >
          <Form.Item
            label="Номер телефона"
            name="phone"
            rules={[
              { required: true, message: "Введите номер" },
              {
                validator: (_, v) => {
                  const digits = digitsOnly(v || "");
                  if (digits.length < 8 || digits.length > 15) {
                    return Promise.reject(new Error("Некорректный номер"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <PhoneInput
              defaultCountry="ru"
              value={phoneInput}
              onChange={(value) => {
                setPhoneInput(value);
                setAuthOptionsChecked(false);
                setAuthHasPassword(false);
                setChannel("voice");
                setShowOtherChannels(false);
                phoneForm.setFieldsValue({ phone: value });
                stopUserCallPolling();
                setUserCallPending(false);
                setUserCallRequestId(null);
                setUserCallNumber(null);
              }}
            />
          </Form.Item>

          {authOptionsChecked && authHasPassword && !showOtherChannels && (
            <>
              <Form.Item
                label="Пароль"
                name="password"
                rules={[
                  { required: true, message: "Введите пароль" },
                  { min: 8, message: "Минимум 8 символов" },
                ]}
              >
                <Input.Password autoComplete="current-password" placeholder="Ваш пароль" />
              </Form.Item>
            </>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
            >
              {!authOptionsChecked
                ? "Продолжить"
                : authHasPassword && !showOtherChannels
                  ? "Войти"
                  : "Позвонить для входа"}
            </Button>
            {authOptionsChecked && authHasPassword && !showOtherChannels && (
              <Button
                type="link"
                className="auth-secondary-link"
                onClick={() => {
                  setShowOtherChannels(true);
                  setChannel("user_call");
                }}
                block
              >
                Другой способ
              </Button>
            )}
            {authOptionsChecked && authHasPassword && showOtherChannels && (
              <>
                <Button
                  type="link"
                  className="auth-secondary-link"
                  onClick={() => {
                    setShowOtherChannels(false);
                    setChannel("password");
                  }}
                  block
                >
                  Войти по паролю
                </Button>
                <Button
                  type="link"
                  className="auth-secondary-link auth-secondary-link--muted"
                  onClick={() => {
                    setUsePasswordLogin(true);
                    setResetMode(true);
                    setResetCodeSent(false);
                    setResetToken(null);
                    resetPhoneForm.setFieldsValue({ phone: phoneInput, channel: "user_call" });
                  }}
                  block
                >
                  Восстановить пароль
                </Button>
              </>
            )}
            {authOptionsChecked && !authHasPassword && (
              <Button
                type="link"
                className="auth-secondary-link"
                onClick={resendCodeByVoice}
                disabled={loading}
                block
              >
                Получить звонок с кодом
              </Button>
            )}
          </Form.Item>
        </Form>
      )}

      {/* ───── Шаг 2. Код ───── */}
      {step === 1 && codeSent && (
        <Form
          key="code"
          form={codeForm}
          layout="vertical"
          onFinish={verifyCode}
        >
          {userCallPending && userCallNumber && (
            <div className="user-call-box">
              <div className="user-call-title">Позвоните для подтверждения</div>
              <div className="user-call-text">С указанного номера позвоните на:</div>
              <a className="user-call-number" href={`tel:+${userCallNumber}`}>
                +{userCallNumber}
              </a>
              <div className="user-call-note">После звонка вход подтвердится автоматически.</div>
            </div>
          )}

          {!userCallPending && (
            <Form.Item
              label="Код подтверждения"
              name="code"
              rules={[
                { required: true, message: "Введите код" },
                {
                  pattern: /^\d{4}$/,
                  message: "Код должен состоять из 4 цифр",
                },
              ]}
            >
              <Input
                maxLength={4}
                inputMode="numeric"
                placeholder="4 цифры"
              />
            </Form.Item>
          )}

          {!userCallPending && (
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Подтвердить код
              </Button>
              <Button type="link" className="auth-secondary-link" onClick={startUserCallAuth} block>
                Подтвердить звонком
              </Button>
              <Button type="link" className="auth-secondary-link auth-secondary-link--muted" onClick={resendCodeByVoice} disabled={loading} block>
                Отправить код ещё раз
              </Button>
            </Form.Item>
          )}
        </Form>
      )}

      {/* ───── Шаг 3. ФИО ───── */}
      {step === 2 && (
        <Form
          key="fio"
          form={fioForm}
          layout="vertical"
          onFinish={completeRegistration}
        >
          <Form.Item
            label="Имя"
            name="first_name"
            rules={[
              { required: true, message: "Введите имя" },
              {
                validator: (_, v) =>
                  !v || /^[А-Яа-яЁёA-Za-z\s-]+$/.test(v)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Недопустимые символы в имени")
                      ),
              },
            ]}
          >
            <Input placeholder="Ваше имя" />
          </Form.Item>

          <Form.Item
            label="Фамилия"
            name="last_name"
            rules={[{ required: true, message: "Введите фамилию" }]}
          >
            <Input placeholder="Ваша фамилия" />
          </Form.Item>

          <Form.Item
            label="Пароль"
            name="password"
            rules={[
              { required: true, message: "Придумайте пароль" },
              { min: 8, message: "Минимум 8 символов" },
            ]}
            hasFeedback
          >
            <Input.Password autoComplete="new-password" placeholder="Минимум 8 символов" />
          </Form.Item>

          <Form.Item
            label="Повторите пароль"
            name="password_confirm"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Повторите пароль" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Пароли не совпадают"));
                },
              }),
            ]}
            hasFeedback
          >
            <Input.Password autoComplete="new-password" placeholder="Повторите пароль" />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
            >
              Завершить регистрацию
            </Button>
          </Form.Item>
        </Form>
      )}

      {/* ───── Шаг 4. Пароль после OTP ───── */}
      {step === 3 && (
        <Form
          key="otp-password"
          form={passwordForm}
          layout="vertical"
          onFinish={completeOtpPasswordAuth}
        >
          <Paragraph type="secondary" style={{ marginBottom: 20, fontSize: 16 }}>
            {passwordMode === "set"
              ? "Телефон подтверждён. Придумайте пароль для следующих входов."
              : "Телефон подтверждён. Введите пароль, чтобы завершить вход."}
          </Paragraph>

          <Form.Item
            label="Пароль"
            name="password"
            rules={[
              { required: true, message: "Введите пароль" },
              { min: 8, message: "Минимум 8 символов" },
            ]}
            hasFeedback
          >
            <Input.Password
              autoComplete={passwordMode === "set" ? "new-password" : "current-password"}
              placeholder="Минимум 8 символов"
            />
          </Form.Item>

          {passwordMode === "set" && (
            <Form.Item
              label="Повторите пароль"
              name="password_confirm"
              dependencies={["password"]}
              rules={[
                { required: true, message: "Повторите пароль" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Пароли не совпадают"));
                  },
                }),
              ]}
              hasFeedback
            >
              <Input.Password autoComplete="new-password" placeholder="Повторите пароль" />
            </Form.Item>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
            >
              {passwordMode === "set" ? "Сохранить пароль и войти" : "Войти"}
            </Button>
          </Form.Item>
        </Form>
      )}

      {step === 1 && (
        <SupportContacts className="auth-support-contacts" />
      )}

    </div>
  );
};

export default RegisterUser;
